import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SubPageHeader } from "@/components/SubPageHeader";
import { Shield, UserCog, Clock, Star, AlertCircle } from "lucide-react";
import { Navigate } from "react-router-dom";

const RESOURCE_LABEL = {
  user: "Employee",
  attendance: "Attendance",
  holiday: "Holiday",
};

const ACTION_COLOR = {
  edit: "#4F8EF7",
  create: "#22D3A5",
  delete: "#F75A5A",
};

const TRACKED_KEYS = [
  "check_in", "check_out", "name", "mobile", "email", "department",
  "designation", "role", "type", "date",
];

function ResourceIcon({ resource }) {
  if (resource === "user") return <UserCog size={16} style={{ color: "#9B6FEB" }} />;
  if (resource === "attendance") return <Clock size={16} style={{ color: "#22D3A5" }} />;
  if (resource === "holiday") return <Star size={16} style={{ color: "#F7C94F" }} />;
  return <AlertCircle size={16} style={{ color: "#7A85A3" }} />;
}

function diffPairs(before, after) {
  const out = [];
  const b = before || {};
  const a = after || {};
  const keys = new Set([...Object.keys(b), ...Object.keys(a)].filter((k) => TRACKED_KEYS.includes(k)));
  for (const k of keys) {
    if (b[k] !== a[k] && (b[k] || a[k])) {
      out.push({ key: k, before: b[k], after: a[k] });
    }
  }
  return out;
}

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const fmtVal = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "string" && v.length > 19 && v.includes("T")) {
    // Likely ISO timestamp → show HH:MM
    try { return new Date(v).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); } catch { return v; }
  }
  return String(v);
};

export default function AuditLog() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "admin") return;
    (async () => {
      try {
        const { data } = await api.get("/audit-log?limit=200");
        setItems(data || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-[100svh]" style={{ background: "#0A0D14", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="audit-log-page">
      <SubPageHeader title="HR Activity Log" />
      <div className="px-4 py-4 space-y-2">
        <div className="rounded-2xl p-3 flex items-center gap-2 text-[11px]" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}>
          <Shield size={14} style={{ color: "#22D3A5" }} />
          Every edit/delete made by HR Managers (when you grant permission in Settings) is recorded here.
        </div>

        {loading && <div className="text-center py-8 text-sm" style={{ color: "#7A85A3" }}>Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-sm" style={{ color: "#7A85A3" }}>No HR edits recorded yet.</div>
        )}

        {items.map((it) => {
          const changes = diffPairs(it.before, it.after);
          const color = ACTION_COLOR[it.action] || "#E8ECFF";
          return (
            <div key={it.id} className="rounded-2xl p-3 space-y-2" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid={`audit-${it.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <ResourceIcon resource={it.resource} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm" style={{ color: "#E8ECFF" }}>
                    <b>{it.actor_name}</b>{" "}
                    <span className="font-mono text-[11px] uppercase" style={{ color }}>{it.action}</span>{" "}
                    <span style={{ color: "#7A85A3" }}>{RESOURCE_LABEL[it.resource] || it.resource}</span>
                  </div>
                  <div className="text-[10px] font-mono" style={{ color: "#7A85A3" }}>
                    {fmtTs(it.at)} · id {(it.resource_id || "").slice(0, 8)}
                  </div>
                </div>
              </div>
              {changes.length > 0 && (
                <div className="space-y-1 pl-10">
                  {changes.map((c) => (
                    <div key={c.key} className="text-[11px]" style={{ color: "#E8ECFF" }}>
                      <span className="text-[10px] uppercase font-mono mr-1" style={{ color: "#7A85A3" }}>{c.key}</span>
                      <span style={{ color: "#F75A5A" }}>{fmtVal(c.before)}</span>
                      <span style={{ color: "#7A85A3" }}> → </span>
                      <span style={{ color: "#22D3A5" }}>{fmtVal(c.after)}</span>
                    </div>
                  ))}
                </div>
              )}
              {it.action === "delete" && it.before && (
                <div className="text-[11px] pl-10" style={{ color: "#7A85A3" }}>
                  Deleted: {it.before.name || it.before.date || it.before.user_name || "row"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
